import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import { createApiGateway } from '../apiGateway';
import {
  type UsersyncStatisticsFilters,
  type UsersyncStatisticsParams,
  type UsersyncStatisticsResponse,
  type UsersyncStatisticsTableRow,
} from '../types/usersyncStatistics';
import { toNumber } from '../helpers/number';

const apiGateway = createApiGateway();

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
const NON_PAGINATED_GROUP_BY = new Set(['sspId', 'dspId', 'stage', 'status']);

type SortDirection = 'ASC' | 'DESC';

interface OrderState {
  by?: string;
  direction?: SortDirection;
}

const shouldUsePagination = (filters: UsersyncStatisticsFilters): boolean => {
  const groupBy = filters.groupBy ?? [];
  if (groupBy.length === 0) {
    return false;
  }

  return groupBy.some((field) => !NON_PAGINATED_GROUP_BY.has(field));
};

const getDefaultOrder = (filters: UsersyncStatisticsFilters): OrderState => {
  const isTimeSeries = filters.format === 'by_day' || filters.format === 'by_hour';

  return isTimeSeries ? { by: 'datetime', direction: 'ASC' } : { by: 'mappingStored', direction: 'DESC' };
};

const initialFilters: UsersyncStatisticsFilters = {
  dateRange: [dayjs().format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')],
  format: 'summary',
  groupBy: null,
  sspId: null,
  dspId: null,
  stage: null,
  status: null,
  reason: null,
  tagType: null,
};

type PaginationState = {
  current: number;
  pageSize: number;
};

type FetchOptions = {
  page?: number;
  pageSize?: number;
  resetPagination?: boolean;
  order?: OrderState;
};

interface ChangeSortOptions {
  columnKey?: string;
  direction?: SortDirection;
  page?: number;
  pageSize?: number;
}

const toOptionalNumber = (value: unknown): number | undefined => {
  if (value == null) return undefined;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/,/g, '').trim();
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : undefined;
  }
  return undefined;
};

export const useUsersyncStatisticsData = () => {
  const [pendingFilters, setPendingFilters] = useState<UsersyncStatisticsFilters>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<UsersyncStatisticsFilters>(initialFilters);
  const [data, setData] = useState<UsersyncStatisticsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [shouldPaginate, setShouldPaginate] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [lastOffset, setLastOffset] = useState<number>(0);
  const [order, setOrder] = useState<OrderState>(() => getDefaultOrder(initialFilters));
  const paginationRef = useRef<PaginationState>(pagination);
  const orderRef = useRef<OrderState>(getDefaultOrder(initialFilters));

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  useEffect(() => {
    orderRef.current = order;
  }, [order]);

  const fetchData = useCallback(async (filtersToApply: UsersyncStatisticsFilters, options: FetchOptions = {}) => {
    try {
      setLoading(true);
      setError(null);

      const params: UsersyncStatisticsParams = {
        from: filtersToApply.dateRange[0],
        to: dayjs(filtersToApply.dateRange[1]).add(1, 'day').format('YYYY-MM-DD'),
        format: filtersToApply.format !== 'summary' ? filtersToApply.format : undefined,
      };

      if (filtersToApply.groupBy && filtersToApply.groupBy.length > 0) {
        params.groupBy = filtersToApply.groupBy.join(',');
      }

      (['sspId', 'dspId', 'stage', 'status', 'reason', 'tagType'] as const).forEach((field) => {
        const value = filtersToApply[field];
        if (value && value.length > 0) {
          params[field] = value.join(',');
        }
      });

      const requiresPagination = shouldUsePagination(filtersToApply);
      const currentPagination = paginationRef.current;
      const nextPageSize = options.pageSize ?? (requiresPagination ? currentPagination.pageSize : DEFAULT_PAGE_SIZE);
      let nextPage = 1;

      if (requiresPagination) {
        if (options.page !== undefined) {
          nextPage = options.page;
        } else if (options.resetPagination) {
          nextPage = 1;
        } else {
          nextPage = currentPagination.current;
        }
      }

      const nextOffset = requiresPagination ? (nextPage - 1) * nextPageSize : 0;

      if (requiresPagination) {
        params.limit = nextPageSize;
        params.offset = nextOffset;
      }

      const defaultOrder = getDefaultOrder(filtersToApply);
      const baseOrder = options.order ?? orderRef.current;
      const nextOrder: OrderState = {
        by: baseOrder.by ?? defaultOrder.by,
        direction: baseOrder.by ? (baseOrder.direction ?? defaultOrder.direction) : undefined,
      };

      if (nextOrder.by) {
        params.orderBy = nextOrder.by;
      }
      if (nextOrder.direction) {
        params.orderDirection = nextOrder.direction;
      }

      const response = await apiGateway.usersyncStatistics.getUsersyncStatistics(params);
      setData(response);
      setAppliedFilters(filtersToApply);
      setShouldPaginate(requiresPagination);
      setTotalCount(typeof response.count === 'number' ? response.count : (response.rows?.length ?? 0));
      setPagination(
        requiresPagination ? { current: nextPage, pageSize: nextPageSize } : { current: 1, pageSize: nextPageSize }
      );
      setLastOffset(nextOffset);
      orderRef.current = nextOrder;
      setOrder(nextOrder);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch usersync statistics');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(initialFilters, { resetPagination: true });
  }, [fetchData]);

  const updatePendingFilters = useCallback((newFilters: Partial<UsersyncStatisticsFilters>) => {
    setPendingFilters((prevFilters) => ({
      ...prevFilters,
      ...newFilters,
    }));
  }, []);

  const hasUnappliedChanges = useMemo(
    () => JSON.stringify(pendingFilters) !== JSON.stringify(appliedFilters),
    [pendingFilters, appliedFilters]
  );

  const applyFilters = useCallback(() => {
    const requiresPagination = shouldUsePagination(pendingFilters);
    const appliedRequiresPagination = shouldUsePagination(appliedFilters);
    const shouldResetPagination = requiresPagination !== appliedRequiresPagination || hasUnappliedChanges;
    const formatChanged = pendingFilters.format !== appliedFilters.format;
    const nextDefaultOrder = formatChanged ? getDefaultOrder(pendingFilters) : undefined;

    fetchData(pendingFilters, {
      resetPagination: shouldResetPagination || formatChanged,
      order: nextDefaultOrder,
    });
  }, [fetchData, pendingFilters, appliedFilters, hasUnappliedChanges]);

  const changePage = useCallback(
    (page: number, pageSize: number) => {
      fetchData(appliedFilters, { page, pageSize });
    },
    [fetchData, appliedFilters]
  );

  const changeSort = useCallback(
    ({ columnKey, direction, page, pageSize }: ChangeSortOptions) => {
      const nextOrder = columnKey && direction ? { by: columnKey, direction } : getDefaultOrder(appliedFilters);
      fetchData(appliedFilters, {
        order: nextOrder,
        page,
        pageSize,
        resetPagination: page === undefined,
      });
    },
    [fetchData, appliedFilters]
  );

  const tableData = useMemo<UsersyncStatisticsTableRow[]>(() => {
    if (!data?.rows) return [];

    const baseIndex = lastOffset;
    return data.rows.map((row, index) => ({
      key: `${baseIndex + index + 1}`,
      index: baseIndex + index + 1,
      ...row,
      initialRequest: toNumber(row.initialRequest),
      eligibleForSync: toNumber(row.eligibleForSync),
      syncAttemptGenerated: toNumber(row.syncAttemptGenerated),
      callbackReceived: toNumber(row.callbackReceived),
      mappingStored: toNumber(row.mappingStored),
      dropCount: toNumber(row.dropCount),
      callbackRate: toOptionalNumber(row.callbackRate),
      storeRate: toOptionalNumber(row.storeRate),
      endToEndRate: toOptionalNumber(row.endToEndRate),
      dropRate: toOptionalNumber(row.dropRate),
    }));
  }, [data, lastOffset]);

  return {
    pendingFilters,
    appliedFilters,
    updatePendingFilters,
    applyFilters,
    hasUnappliedChanges,
    tableData,
    loading,
    error,
    shouldPaginate,
    pagination,
    totalCount,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    changePage,
    changeSort,
    order,
  };
};
